import { useCallback, useState } from 'react';

import { api } from '../live/api.ts';
import { clockStampSeconds, compactDuration } from '../lib/format.ts';
import { Spinner } from './primitives.tsx';
import './transcript-summary.css';

export interface TranscriptSummaryState {
  summary: string;
  model: string;
  bytes: number;
  coversUpTo: number;
  generatedAt: number;
  durationMs: number;
}

export interface TranscriptSummaryHook {
  summary: TranscriptSummaryState | null;
  busy: boolean;
  error: string | null;
  /** Ask for a summary. Safe to call again; the server shares one run per attempt. */
  summarise: () => Promise<void>;
  /** Drop everything, for when the pane is pointed at a different attempt. */
  reset: () => void;
  /** `Summarise` the first time, `Re-summarise` after that. */
  label: string;
}

/**
 * Summariser state for one attempt's transcript pane.
 *
 * A hook rather than a component because the two panes that use it — the Runs ledger and
 * the ticket drawer's Activity tab — put the button in headers that look nothing alike.
 * They share the state machine and the rendered summary; they do not share a toolbar.
 */
export function useTranscriptSummary(runId: number): TranscriptSummaryHook {
  const [summary, setSummary] = useState<TranscriptSummaryState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const summarise = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setSummary(await api.summariseTranscript(runId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }, [runId]);

  const reset = useCallback(() => {
    setSummary(null);
    setError(null);
  }, []);

  return {
    summary,
    busy,
    error,
    summarise,
    reset,
    label: busy ? 'Summarising…' : summary ? 'Re-summarise' : 'Summarise',
  };
}

/**
 * The model's reading of the transcript tail, above the transcript itself.
 *
 * Three states worth distinguishing and no more: nothing asked for yet (render nothing, the
 * button is the affordance), working, and a result. The result carries how much of the file
 * it read and whether the file has grown since, because the common case for pressing that
 * button is a run that is still going — a summary that silently describes a window which has
 * already moved on is worse than no summary.
 */
export function TranscriptSummary({
  summary,
  busy,
  error,
  transcriptSize,
}: {
  summary: TranscriptSummaryState | null;
  busy: boolean;
  error: string | null;
  /** Current size on disk, so a summary that has fallen behind can say so. Null when unknown. */
  transcriptSize: number | null;
}) {
  if (busy) {
    return (
      <div className="runs-summary" aria-busy="true">
        <Spinner size={14} />
        <span className="sheet-note">Reading the transcript…</span>
      </div>
    );
  }

  const behind =
    summary === null || transcriptSize === null ? 0 : Math.max(0, transcriptSize - summary.coversUpTo);
  return (
    <>
      {/* A failed re-summarise does not take the previous summary down with it. The prose is
          the thing the operator was reading, and "the CLI is missing" does not make it wrong. */}
      {error ? (
        <div className="runs-summary runs-summary-error" role="alert">
          <span style={{ color: 'var(--danger-text)' }}>{error}</span>
        </div>
      ) : null}
      {summary ? (
        <div className="runs-summary">
          <p className="runs-summary-text">{summary.summary}</p>
          <p className="sheet-note">
            {summary.model} · read {Math.max(1, Math.round(summary.bytes / 1024))} KB ·{' '}
            {compactDuration(summary.durationMs)} · {clockStampSeconds(summary.generatedAt)}
            {behind > 0 ? ` · the transcript has grown ${Math.round(behind / 1024)} KB since` : ''}
          </p>
        </div>
      ) : null}
    </>
  );
}
